import Swiper, { Keyboard, Lazy, Navigation, Thumbs } from "swiper";
import { disableScroll, enableScroll } from "../../components/_helpers";

const IMAGES_SLIDER_SELECTOR = ".XXX-images-slider",
  OPEN_BUTTON_SELECTOR = ".open-images-modal";

type SliderTypes = "users" | "default";

type Tabs = {
  [key in SliderTypes]: {
    initialized: boolean;
    slider: Swiper | null;
    thumbs: HTMLElement[] | null;
    prevIndex: number;
  };
};

class Lightbox {
  private tabs: Tabs = {
    users: {
      initialized: false,
      slider: null,
      thumbs: null,
      prevIndex: 0,
    },
    default: {
      initialized: false,
      slider: null,
      thumbs: null,
      prevIndex: 0,
    },
  };

  private active = false;
  private modalElement: HTMLElement | null;

  private currentType: SliderTypes | null = null;

  constructor() {
    this.modalElement = null;
  }

  init = async () => {
    this.modalElement = document.querySelector(".images-modal") as HTMLElement;
    await this.initHandlers();
    await this.tabsHandler();

    window.addEventListener("product_hot_reload", () => {
      this.destroy();

      setTimeout(async () => {
        this.modalElement = document.querySelector(".images-modal") as HTMLElement;
        await this.initHandlers();
        await this.tabsHandler();
      }, 0);
    });
  };

  initHandlers = async (): Promise<void> => {
    await this.openHandler();
    await this.handleReviewsImages();
    await this.closeHandler();

    document.addEventListener("get_reviews", () => {
      this.handleReviewsImages();
    });
  };

  openHandler = async (): Promise<void> => {
    const canOpenElements = [...document.querySelectorAll<HTMLElement>(OPEN_BUTTON_SELECTOR)];

    if (canOpenElements.length === 0) return;

    canOpenElements.forEach((element) => {
      element.addEventListener("click", async () => {
        if (element.querySelector("lite-youtube") && !element.classList.contains("yt-started")) {
          element.classList.add("yt-started");
          return;
        }

        const type = element.dataset.type as SliderTypes;
        const imageId = element.dataset.id as string;

        this.currentType !== type && (await this.changeTab(type));
        this.changeActiveTabById(type, imageId);
        this.openAction(await this.findSlideByImageId(type, imageId));
      });
    });
  };

  handleReviewsImages = async (): Promise<void> => {
    const canOpenElements = [...document.querySelectorAll<HTMLElement>(".open-images-review")];

    if (canOpenElements.length === 0) return;

    canOpenElements.forEach((element) => {
      const clickEvent = async () => {
        const type = element.dataset.type as SliderTypes;
        const imageId = element.dataset.id as string;

        this.currentType !== type && (await this.changeTab(type));
        this.changeActiveTabById(type, imageId);
        this.openAction(await this.findSlideByImageId(type, imageId));
      };
      element.addEventListener("click", clickEvent);

      document.addEventListener("get_reviews", () => {
        element.removeEventListener("click", clickEvent);
      });
    });
  };

  changeTab = async (type: SliderTypes): Promise<void> => {
    await this.initialize(type);
    await this.hideCurrentTab();
    await this.showTab(type);
    this.currentType = type;
  };

  hideCurrentTab = async (): Promise<void> => {
    if (!this.currentType) return;

    const sliderElement = this.tabs[this.currentType].slider?.$el[0];
    sliderElement?.classList.add("hidden");

    const thumbsElement = this.modalElement?.querySelector<HTMLElement>(`.${this.currentType}-tab-content`);
    thumbsElement?.classList.add("hidden");

    const tabTitle = this.modalElement?.querySelector<HTMLElement>(`.${this.currentType}-tab-title`);
    tabTitle?.classList.remove("active");
  };

  showTab = async (type: SliderTypes): Promise<void> => {
    const sliderElement = this.tabs[type].slider?.$el[0];
    sliderElement?.classList.remove("hidden");

    const thumbsElement = this.modalElement?.querySelector<HTMLElement>(`.${type}-tab-content`);
    thumbsElement?.classList.remove("hidden");

    const tabTitle = this.modalElement?.querySelector<HTMLElement>(`.${type}-tab-title`);
    tabTitle?.classList.add("active");

    this.tabs[type].slider?.update();
  };

  initialize = async (type: SliderTypes): Promise<void> => {
    if (this.tabs[type].initialized) return;

    const slider = new Swiper(IMAGES_SLIDER_SELECTOR.replace("XXX", type), {
      slidesPerView: 1,
      lazy: true,
      watchSlidesProgress: true,
      preloadImages: false,
      navigation: {
        nextEl: `.${type}-next`,
        prevEl: `.${type}-prev`,
      },
      keyboard: {
        enabled: true,
      },
      modules: [Navigation, Lazy, Keyboard],
    });

    slider.on("slideChange", async () => {
      const activeThumb = await this.getActiveThumb(type);
      activeThumb && (this.tabs[type].prevIndex = Number(activeThumb.dataset.index) as number);

      const prevSlide = this.tabs[type].slider?.slides[this.tabs[type].prevIndex] as HTMLElement;
      const prevVideo = prevSlide.querySelector("video"),
        prevYouTube = prevSlide.querySelector("lite-youtube");

      prevVideo && prevVideo.pause();

      if (prevYouTube) {
        const iframe = prevYouTube.querySelector("iframe");
        iframe?.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
      }

      this.changeActiveTabByIndex(type, String(slider.activeIndex) as string);
    });

    if (!this.modalElement) return;

    const thumbs = [...this.modalElement?.querySelectorAll<HTMLElement>(`.${type}-images .images-list__item`)];

    this.tabs[type] = {
      initialized: true,
      slider: slider,
      thumbs: thumbs,
      prevIndex: 0,
    };

    this.initThumsEvents(type);
  };

  initThumsEvents = async (type: SliderTypes): Promise<void> => {
    const thumbs = this.tabs[type].thumbs;

    if (!thumbs) return;

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", async () => {
        this.tabs[type].prevIndex = this.tabs[type].slider?.activeIndex as number;
        this.tabs[type].slider?.slideTo(thumb.dataset.index as unknown as number, 0);
      });
    });
  };

  openAction = async (slideToFocus: number | null = null): Promise<void> => {
    disableScroll();
    this.active = true;

    this.modalElement?.classList.add("open");

    if (slideToFocus === null) return;
    this.tabs[this.currentType as SliderTypes].prevIndex = this.tabs[this.currentType as SliderTypes].slider?.activeIndex as number;
    this.tabs[this.currentType as SliderTypes].slider?.slideTo(slideToFocus, 0);
  };

  closeHandler = async (): Promise<void> => {
    if (this.modalElement === null) return;
    const canCloseElements = [...this.modalElement.querySelectorAll<HTMLElement>(".close-images-modal")];

    if (canCloseElements.length === 0) return;

    canCloseElements.forEach((element) => {
      element.addEventListener("click", () => {
        this.closeAction();
      });
    });

    window.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.active) {
        this.closeAction();
      }
    });
  };

  closeAction = async (): Promise<void> => {
    this.active = false;
    this.modalElement?.classList.remove("open");
    enableScroll();
  };

  findSlideByImageId = async (type: SliderTypes, id: string): Promise<number | null> => {
    const slider = this.tabs[type].slider;

    if (!slider) return null;

    const slides = [...slider.$el[0].querySelectorAll<HTMLElement>(".swiper-slide")];
    const slideIndex = slides.findIndex((slide) => slide.dataset.id === id);

    return slideIndex;
  };

  tabsHandler = async (): Promise<void> => {
    if (this.modalElement === null) return;
    const tabsTogglers = [...this.modalElement.querySelectorAll<HTMLElement>(".tabs__list--title")];

    if (tabsTogglers.length === 0) return;

    tabsTogglers.forEach((toggler) => {
      toggler.addEventListener("click", async () => {
        if (this.currentType === toggler.dataset.type) return;

        const type = toggler.dataset.type as SliderTypes;
        await this.changeTab(type);
        this.tabs[type].prevIndex = this.tabs[type].slider?.activeIndex as number;
        this.tabs[type].slider?.slideTo(0, 0);
      });
    });
  };

  changeActiveTabById = async (type: SliderTypes, id: string): Promise<void> => {
    const thumb = this.tabs[type].thumbs?.find((thumb) => thumb.dataset.id === id);
    if (!thumb) return;

    this.changeActiveThumb(type, thumb);
  };

  changeActiveTabByIndex = async (type: SliderTypes, index: string): Promise<void> => {
    const thumb = this.tabs[type].thumbs?.find((thumb) => thumb.dataset.index === index);
    if (!thumb) return;

    this.changeActiveThumb(type, thumb);
  };

  changeActiveThumb = async (type: SliderTypes, thumb: HTMLElement): Promise<void> => {
    const activeThumb = await this.getActiveThumb(type);
    activeThumb?.classList.remove("active");
    thumb.classList.add("active");

    const thumbsWrapper = this.modalElement?.querySelector<HTMLElement>(`.${type}-images.images-list`);

    if (!thumbsWrapper) return;

    const direction = this.tabs[type].prevIndex < Number(thumb.dataset.index) ? "right" : "left";

    let scrollLeft = 0,
      scrollTop = 0,
      isElementHorizontalHidden = false,
      isElementVerticalHidden = false;

    const diff = (this.tabs[type].prevIndex % 3) - (Number(thumb.dataset.index) % 3);
    const verticalDirection = Math.abs(diff) === 2 ? (diff < 0 ? "up" : "down") : "none";

    if (verticalDirection === "up") {
      scrollTop = thumb.offsetTop - 16;
      isElementVerticalHidden = thumb.offsetTop - thumbsWrapper.scrollTop < 0;
    } else if (verticalDirection === "down") {
      scrollTop = thumb.offsetTop - thumbsWrapper.offsetHeight + thumb.offsetHeight + 16;
      isElementVerticalHidden = thumb.offsetTop + thumb.offsetHeight - thumbsWrapper.scrollTop > thumbsWrapper.offsetHeight;
    }

    if (direction === "right") {
      scrollLeft = thumb.offsetLeft - thumbsWrapper.offsetWidth + thumb.offsetWidth + 12;
      isElementHorizontalHidden = thumb.offsetLeft + thumb.offsetWidth - thumbsWrapper.scrollLeft > thumbsWrapper.offsetWidth;
    } else if (direction === "left") {
      scrollLeft = thumb.offsetLeft - 12;
      isElementHorizontalHidden = thumb.offsetLeft - thumbsWrapper.scrollLeft < 0;
    }

    if (isElementHorizontalHidden) {
      thumbsWrapper.scrollTo({
        left: scrollLeft,
        behavior: "smooth",
      });
    }

    if (isElementVerticalHidden) {
      thumbsWrapper.scrollTo({
        top: scrollTop,
        behavior: "smooth",
      });
    }
  };

  getActiveThumb = async (type: SliderTypes): Promise<HTMLElement | null> => {
    const thumbs = this.tabs[type].thumbs;

    if (!thumbs) return null;

    return [...thumbs].find((thumb) => thumb.classList.contains("active")) || null;
  };

  destroy = async (): Promise<void> => {
    this.modalElement = null;
    this.active = false;
    this.currentType = null;
  };
}

export default Lightbox;
